<template>
  <v-container fluid class="ml-lg-4">
    <v-card>
      <v-toolbar>
        <v-toolbar-title>{{ t("campaign.list.title") }}</v-toolbar-title>
      </v-toolbar>

      <v-container fluid>
        <v-row align="center" justify="center" class="my-n2" dense>
          <v-col cols="6" lg="2">
            <v-text-field
              v-model="filter.campaign"
              :label="t('campaign.list.filter_campaign')"
            />
          </v-col>
          <v-col cols="6" lg="2">
            <v-select
              v-model="filter.type"
              :items="filterOptions.type"
              :label="t('campaign.list.filter_type')"
              clearable
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:prepend>
                    <v-icon>{{ t("campaign.icon." + item.raw) }}</v-icon>
                  </template>
                  <template v-slot:title>
                    {{ t("campaign.type." + item.raw) }}
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <template v-if="item.value">
                    <v-icon>{{ t("campaign.icon." + item.value) }}</v-icon>
                    {{ t("campaign.type." + item.value) }}
                </template>
              </template>
            </v-select>
          </v-col>
          <v-col cols="6" lg="2">
            <v-select
              v-model="filter.status"
              :items="filterOptions.status"
              :label="t('campaign.list.filter_status')"
              clearable
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:title>
                    {{ t("campaign.list.status__" + item.raw) }}
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <template v-if="item.value">
                  {{ t("campaign.list.status__" + item.value) }}
                </template>
              </template>
            </v-select>
          </v-col>
          <v-col cols="6" lg="2">
            <v-select
              v-model="filter.runtime"
              :items="filterOptions.runtime"
              :label="t('campaign.list.filter_runtime')"
              clearable
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:title>
                    {{ t("campaign.list.phase__" + item.raw) }}
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <template v-if="item.value">
                  {{ t("campaign.list.phase__" + item.value) }}
                </template>
              </template>
            </v-select>
          </v-col>
          <v-col cols="6" lg="2">
            <v-select
              v-model="filter.order_by"
              :items="filterOptions.orderBy"
              :label="t('campaign.list.filter_order_by')"
            >
              <template v-slot:item="{ props, item }">
                <v-list-item v-bind="props">
                  <template v-slot:title>
                    {{ t("campaign.list.order_by__" + item.raw) }}
                  </template>
                </v-list-item>
              </template>
              <template v-slot:selection="{ item }">
                <template v-if="item.value">
                  {{ t("campaign.list.order_by__" + item.value) }}
                </template>
              </template>
            </v-select>
          </v-col>
        </v-row>

        <v-data-table
          :items="rows"
          :items-per-page="pager.entries_per_page"
          :items-length="pager.total_entries"
          hide-default-footer
          :headers="headers"
        >
          <template v-slot:headers="{ columns }">
            <tr>
              <th v-for="column in columns" :key="column.key">
                {{ t("campaign.list.th_" + column.key) }}
              </th>
            </tr>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <td style="vertical-align: middle">
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <v-icon v-bind="tooltipProps">
                      {{ t("campaign.icon." + item.type) }}
                    </v-icon>
                  </template>
                  <span>
                    {{ t("campaign.type." + item.type) }} ({{ item.type }})
                  </span>
                </v-tooltip>
              </td>
              <td style="vertical-align: middle">
                <img
                  v-if="item.image && item.campaign_image_extension"
                  :src="item.image.src"
                />
                <v-icon v-else size="large">mdi-image-outline</v-icon>
              </td>
              <td>
                <b>{{ item.title[user.locale] }}</b><br />
                <span style="font-size: 75%; font-family: monospace">
                  {{ item.uuid }}
                </span>
              </td>
              <td>
                <b>{{ item.participations }}</b><br />
                {{ item.distinct_users }}
              </td>
              <td>
                {{
                  t("generic.number", {
                    value: item.view_list + item.view_detail + item.view_mail
                  })
                }}
              </td>
              <td>
                <b>{{ t("campaign.list.status__" + item.current_status) }}</b><br />
                {{ formatDatetime(item.modified) }}<br />
                {{ item.email }}
              </td>
              <td :class="colorFromPhase(item.phase, item.current_status)">
                <b
                  v-if="
                    item.current_status !== 'deleted' &&
                    item.current_status !== 'rejected'
                  "
                >
                  {{ t("campaign.list.phase__" + item.phase) }}
                </b>
                <br />
                {{ formatDate(item.valid_from) }}<br />
                {{ formatDate(item.valid_until) }}
              </td>
              <td>
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <v-btn variant="text" icon v-bind="tooltipProps" :to="genLink(item)">
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ t("campaign.list.btn_edit") }}</span>
                </v-tooltip>

                <v-tooltip location="bottom" v-if="merchantLevel >= 1">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <v-btn
                      variant="text"
                      icon
                      v-bind="tooltipProps"
                      @click="toggleListable(item)"
                      :color="item.is_listable ? 'green' : 'red-lighten-2'"
                    >
                      <v-icon>
                        {{ item.is_listable ? "mdi-eye" : "mdi-eye-off" }}
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>
                    {{
                      t(
                        item.is_listable
                          ? "campaign.list.btn_set_unlistable"
                          : "campaign.list.btn_set_listable"
                      )
                    }}
                  </span>
                </v-tooltip>

                <v-tooltip location="bottom" v-if="merchantLevel >= 1">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <v-btn
                      variant="text"
                      icon
                      v-bind="tooltipProps"
                      @click="genericConfirmDialog('clone', item)"
                    >
                      <v-icon>mdi-sync</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ t("campaign.list.btn_clone") }}</span>
                </v-tooltip>

                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <v-btn
                      variant="text"
                      icon
                      v-bind="tooltipProps"
                      :disabled="item.participations ? true : false"
                      @click="genericConfirmDialog('delete', item)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ t("campaign.list.btn_delete") }}</span>
                </v-tooltip>

                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <v-btn
                      variant="text"
                      icon
                      v-bind="tooltipProps"
                      target="_blank"
                      :to="'view-in-app/' + item.uuid"
                    >
                      <v-icon>mdi-open-in-new</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ t("campaign.list.btn_preview") }}</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
          <template v-slot:footer>
            <v-row class="ma-lg-2">
              <v-col cols="12" md="6">
                <v-btn
                  v-if="currentMerchant.uuid"
                  color="primary"
                  :to="{
                    name: 'campaigns-create',
                    params: { merchantIdent: currentMerchant.uuid }
                  }"
                >
                  <v-icon start>mdi-arrow-right</v-icon>
                  {{
                    t("campaign.list.create_for_merchant", {
                      merchant_name: currentMerchant.name
                    })
                  }}
                </v-btn>
              </v-col>

              <v-col cols="12" md="6">
                <div class="text-right">
                  <span class="pa-2">
                    {{
                      t("generic.pager_total_hits", {
                        hits: pager.total_entries
                      })
                    }}
                  </span>
                  <v-btn
                    v-if="prevPage"
                    @click="page = prevPage.page"
                    icon
                    size="small"
                    class="ma-2"
                  >
                    <v-icon>mdi-chevron-left</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="nextPage"
                    @click="page = nextPage.page"
                    icon
                    size="small"
                    class="ma-2"
                  >
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </v-container>
    </v-card>
    <v-dialog v-model="confirmDialog.show" width="80%" persistent>
      <v-card>
        <v-card-title class="text-h5">
          {{ t("campaign.list.confirm_title__" + confirmDialog.type) }}
        </v-card-title>
        <v-card-text>
          {{
            t("campaign.list.confirm_text__" + confirmDialog.type, {
              name: confirmDialog.name
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-btn color="error" @click="confirmNo()">
            {{ t("campaign.list.confirm_btn_no") }}
          </v-btn>
          <v-btn color="primary" @click="confirmYes()">
            {{ t("campaign.list.confirm_btn_yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useCampaignStore } from '@/stores/campaign'
import {
  getCampaignList,
  apiToggleListable,
  apiDeleteCampaign
} from '@/api/campaign.js'
import { formatDate, formatDatetime } from '@/utils/format.js'

const props = defineProps({
  query: {
    type: Object,
    default: () => ({})
  }
})

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const campaignStore = useCampaignStore()
const { $alert } = getCurrentInstance().appContext.config.globalProperties

const rows = ref([])
const page = ref(parseInt(route.query.page) || 1)
const pager = ref({
  links: [],
  entries_per_page: 10,
  total_entries: 0
})
const filter = ref({
  campaign: route.query.campaign || '',
  type: route.query.type || '',
  runtime: route.query.runtime || '',
  status: route.query.status || '',
  order_by: route.query.order_by || 'valid_from'
})

const syncToUrl = () => {
  const query = {}
  if (filter.value.campaign) query.campaign = filter.value.campaign
  if (filter.value.type) query.type = filter.value.type
  if (filter.value.status) query.status = filter.value.status
  if (filter.value.runtime) query.runtime = filter.value.runtime
  if (filter.value.order_by !== 'valid_from') query.order_by = filter.value.order_by
  if (page.value > 1) query.page = page.value
  router.replace({ query })
}

const filterOptions = {
  type: [
    'sweepstake/standard',
    'coupon/free',
    'coupon/absolute',
    'coupon/m_for_n',
    'coupon/offer',
    'coupon/percent',
    'coupon',
    'tip'
  ],
  status: [
    'active',
    'created',
    'approved',
    'start_review',
    'ended',
    'deleted',
    'rejected'
  ],
  runtime: ['running', 'pending', 'missing_approval', 'ended'],
  orderBy: [
    'valid_from',
    'valid_from_reverse',
    'valid_until',
    'valid_until_reverse',
    'modified',
    'modified_reverse',
    'created',
    'created_reverse',
    'title',
    'title_reverse',
    'type',
    'type_reversed',
    'status',
    'status_reversed'
  ]
}

const confirmDialog = ref({
  show: false,
  uuid: '',
  name: '',
  type: ''
})

const confirmMethods = {
  clone: 'doCloneCampaign',
  delete: 'doDeleteCampaign'
}

const user = computed(() => authStore.user)
const merchantLevel = computed(() => authStore.merchantLevel)
const currentMerchant = computed(() => authStore.currentMerchant)

const headers = computed(() => [
  { key: 'type', sortable: false },
  { key: 'image', sortable: false },
  { key: 'campaign', sortable: false },
  { key: 'participations', sortable: false },
  { key: 'views', sortable: false },
  { key: 'status', sortable: false },
  { key: 'phase', sortable: false },
  { key: 'actions', sortable: false }
])

const nextPage = computed(() => {
  return (
    pager.value.links.find((l) => l.rel == 'next') ||
    pager.value.links.find((l) => l.rel == 'last')
  )
})

const prevPage = computed(() => {
  return (
    pager.value.links.find((l) => l.rel == 'prev') ||
    pager.value.links.find((l) => l.rel == 'first')
  )
})

onMounted(() => {
  fetchData()
})

watch(page, () => {
  syncToUrl()
  fetchData()
})

let campaignDebounce = null
watch(() => filter.value.campaign, () => {
  clearTimeout(campaignDebounce)
  campaignDebounce = setTimeout(() => {
    page.value = 1
    syncToUrl()
    fetchData()
  }, 300)
})

watch(
  () => [filter.value.type, filter.value.runtime, filter.value.status, filter.value.order_by],
  () => {
    page.value = 1
    syncToUrl()
    fetchData()
  }
)

watch(currentMerchant, () => {
  page.value = 1
  fetchData()
})

const colorFromPhase = (phase, currentStatus) => {
  if (currentStatus === 'deleted' || currentStatus == 'rejected') {
    return ''
  }
  switch (phase) {
    case 'running':
      if (currentStatus === 'active') {
        return 'light-green lighten-2'
      } else {
        return 'red lighten-3'
      }
    case 'missing_approval':
      return 'red'
    case 'pending':
      return 'yellow lighten-2'
    case 'ended':
      return 'blue-grey lighten-2'
    case 'invalid':
      return 'red lighten-3'
  }
}

const genericConfirmDialog = (action, c) => {
  confirmDialog.value.uuid = c.uuid
  confirmDialog.value.name = c.title[user.value.locale]
  confirmDialog.value.type = action
  confirmDialog.value.show = true
}

const confirmNo = () => {
  confirmReset()
}

const confirmYes = async () => {
  const method = confirmMethods[confirmDialog.value.type]
  if (method === 'doCloneCampaign') {
    await doCloneCampaign()
  } else if (method === 'doDeleteCampaign') {
    await doDeleteCampaign()
  }
  confirmReset()
}

const confirmReset = () => {
  confirmDialog.value.show = false
  confirmDialog.value.uuid = ''
  confirmDialog.value.name = ''
  confirmDialog.value.type = ''
}

const doCloneCampaign = async () => {
  await campaignStore.doCloneCampaign(confirmDialog.value.uuid)
}

const fetchData = async () => {
  const res = await getCampaignList({
    page: page.value,
    filter_campaign: filter.value.campaign,
    filter_type: filter.value.type,
    filter_status: filter.value.status,
    filter_runtime: filter.value.runtime,
    exact_merchant: currentMerchant.value.uuid,
    order_by: filter.value.order_by
  })
  if (res) {
    rows.value = res.rows
    pager.value = res.pager
  }
}

const toggleListable = async (c) => {
  const status = await apiToggleListable({ campaignIdent: c.uuid })
  c.is_listable = status
}

const doDeleteCampaign = async () => {
  const result = await apiDeleteCampaign({
    campaignIdent: confirmDialog.value.uuid
  })
  if (result.status === 'deleted') {
    $alert({
      message: t('campaign.list.delete_successful')
    })
    fetchData()
  } else {
    $alert({
      message: t('campaign.list.delete_error__' + result.reason),
      type: 'error'
    })
  }
}

const genLink = (item) => {
  let step = '-benefit'
  if (
    [
      'approved',
      'ended',
      'submitted',
      'start_review',
      'deleted',
      'active'
    ].includes(item.current_status)
  ) {
    step = '-review'
  }
  return {
    name: item.type.split('/')[0] + step,
    params: { campaignIdent: item.uuid }
  }
}
</script>

<style scoped>
:deep(td) {
  vertical-align: top;
}
</style>
